// Conexión en vivo del chat interno: la contraparte de
// ApiGlobal/src/services/chatRealtime.js. Sin React, para poder probarla sola.
//
// - Se une con el token de sesión y, si se corta, vuelve a conectarse sola.
// - `enviarMensaje` devuelve una promesa: se cumple con el mensaje guardado cuando el
//   servidor lo confirma (`ack` con el mismo `cid`) y se rechaza si responde con error,
//   si se cae la conexión antes o si la confirmación no llega a tiempo.
// - Lo demás que manda el servidor (mensajes nuevos, lecturas, quién escribe, quién
//   está en línea) se entrega tal cual a `alEvento`.

const SESION_INVALIDA = 4401;
// El API no tiene almacén del chat configurado: volver a intentar no cambia nada.
const NO_CONFIGURADO = 4503;
const REINTENTOS_MS = [1000, 2000, 5000, 10000];
const ESPERA_CONFIRMACION_MS = 15000;

// Sin URL del API (en Docker el front y el API van por el mismo dominio) se usa la de la página.
export const urlChatEnVivo = (apiUrl = process.env.REACT_APP_API_URL || "") =>
  `${(apiUrl || window.location.origin).replace(/\/$/, "").replace(/^http/i, "ws")}/apiglobal/v1/chat/ws`;

/**
 * @param {Object} opciones
 * @param {Function} opciones.obtenerToken - token de sesión en cada (re)conexión
 * @param {Function} opciones.alEvento - (tipo, datos) para la pantalla
 */
export function crearConexionChat({
  obtenerToken,
  alEvento,
  url = urlChatEnVivo(),
  WebSocketImpl = typeof WebSocket !== "undefined" ? WebSocket : null,
  reintentosMs = REINTENTOS_MS,
  esperaConfirmacionMs = ESPERA_CONFIRMACION_MS,
}) {
  let ws = null;
  let conectado = false;
  let terminada = false;
  let tokenUsado = null;
  let intento = 0;
  let temporizadorReconexion = null;
  let siguienteCid = 1;
  // cid -> { resolve, reject, temporizador }
  const pendientes = new Map();

  const emitir = (tipo, datos = {}) => alEvento?.(tipo, datos);
  const enviar = (mensaje) => {
    if (!ws || !conectado) return false;
    ws.send(JSON.stringify(mensaje));
    return true;
  };

  const resolverPendiente = (cid, accion, valor) => {
    const pendiente = pendientes.get(cid);
    if (!pendiente) return;
    pendientes.delete(cid);
    clearTimeout(pendiente.temporizador);
    pendiente[accion](valor);
  };

  // Lo que no alcanzó a confirmarse queda en duda: pudo guardarse o no. La pantalla lo
  // marca como no enviado y, al volver, recarga la conversación.
  const rechazarPendientes = (motivo) => {
    [...pendientes.keys()].forEach((cid) => resolverPendiente(cid, "reject", new Error(motivo)));
  };

  const alMensaje = (evento) => {
    let mensaje;
    try {
      mensaje = JSON.parse(evento.data);
    } catch (error) {
      return;
    }

    switch (mensaje.type) {
      case "listo":
        conectado = true;
        intento = 0;
        emitir("estado", { estado: "conectado" });
        emitir("listo", mensaje);
        break;
      case "ack":
        resolverPendiente(mensaje.cid, "resolve", mensaje.mensaje);
        break;
      case "error":
        if (mensaje.cid !== null && mensaje.cid !== undefined) {
          resolverPendiente(mensaje.cid, "reject", new Error(mensaje.mensaje || "No se pudo enviar el mensaje"));
        }
        break;
      default:
        // mensaje, leido, conversacion, escribiendo, presencia, en_linea, cerrado
        emitir(mensaje.type, mensaje);
    }
  };

  const conectar = () => {
    if (terminada || !WebSocketImpl) return;
    emitir("estado", { estado: intento === 0 ? "conectando" : "reconectando" });

    const socket = new WebSocketImpl(url);
    ws = socket;
    socket.onopen = () => {
      tokenUsado = obtenerToken();
      socket.send(JSON.stringify({ type: "join", token: tokenUsado }));
    };
    socket.onmessage = alMensaje;
    socket.onclose = (evento) => {
      if (ws !== socket) return;
      conectado = false;
      rechazarPendientes("Se perdió la conexión antes de confirmar el envío");
      // Con la sesión vencida solo vale la pena volver si ya hay otro token (se renovó
      // o se volvió a iniciar sesión en otra pestaña).
      if (evento?.code === NO_CONFIGURADO) {
        terminada = true;
        emitir("estado", { estado: "no_configurado" });
        return;
      }
      if (terminada || (evento?.code === SESION_INVALIDA && obtenerToken() === tokenUsado)) {
        terminada = true;
        emitir("estado", { estado: "cerrado" });
        return;
      }
      emitir("estado", { estado: "reconectando" });
      const espera = reintentosMs[Math.min(intento, reintentosMs.length - 1)];
      intento += 1;
      temporizadorReconexion = setTimeout(conectar, espera);
    };
  };

  conectar();

  return {
    /**
     * Con `conversacionId`, en esa conversación; con `destinatarioId`, en la directa con
     * esa persona (el servidor la crea si es la primera vez). `adjunto` es { clave,
     * duracion } de un archivo ya subido al almacén.
     * @returns {Promise<Object>} el mensaje tal como quedó guardado
     */
    enviarMensaje({ conversacionId = null, destinatarioId = null, texto, adjunto = null }) {
      const cid = siguienteCid;
      siguienteCid += 1;
      return new Promise((resolve, reject) => {
        if (!enviar({ type: "mensaje", cid, conversacionId, destinatarioId, texto, ...(adjunto ? { adjunto } : {}) })) {
          reject(new Error("Sin conexión con el chat"));
          return;
        }
        const temporizador = setTimeout(
          () => resolverPendiente(cid, "reject", new Error("No se confirmó el envío")),
          esperaConfirmacionMs
        );
        pendientes.set(cid, { resolve, reject, temporizador });
      });
    },

    leer(conversacionId, hastaId) {
      enviar({ type: "leer", conversacionId, hastaId });
    },

    escribiendo(conversacionId) {
      enviar({ type: "escribiendo", conversacionId });
    },

    cerrar() {
      terminada = true;
      clearTimeout(temporizadorReconexion);
      rechazarPendientes("El chat se cerró");
      ws?.close();
    },

    get conectado() {
      return conectado;
    },
  };
}
